import { useState } from 'react';
import { Deck, DeckFilters } from '../services/api';
import { Chip, ChipGroup, Button } from '@hyggecards/design-system';
import './DeckPicker.css';

interface DeckPickerProps {
  personalDecks: Deck[];
  curatedDecks: Deck[];
  selectedPersonalDecks: string[];
  selectedCuratedDecks: string[];
  onSelectionChange: (filters: DeckFilters) => void;
  loading?: boolean;
}

// Render deck stats as badge content
function DeckStats({
  total,
  struggling,
  newCount,
}: {
  total: number;
  struggling: number;
  newCount: number;
}) {
  return (
    <span className="deck-stats">
      <span className="stat-total">{total}</span>
      {struggling > 0 && (
        <span className="stat-struggling" title="Struggling">
          {struggling}
        </span>
      )}
      {newCount > 0 && (
        <span className="stat-new" title="New">
          {newCount}
        </span>
      )}
    </span>
  );
}

// Generate summary text for collapsed state
function getSelectionSummary(
  personalDecks: Deck[],
  curatedDecks: Deck[],
  selectedPersonalDecks: string[],
  selectedCuratedDecks: string[]
): string {
  const totalSelected = selectedPersonalDecks.length + selectedCuratedDecks.length;

  if (totalSelected === 0) {
    return 'All decks';
  }

  // Get display names for selected decks
  const selectedNames: string[] = [];

  for (const name of selectedCuratedDecks) {
    const deck = curatedDecks.find(d => d.name.toLowerCase() === name);
    if (deck) selectedNames.push(deck.name);
  }

  for (const name of selectedPersonalDecks) {
    const deck = personalDecks.find(d => d.name.toLowerCase() === name);
    if (deck) selectedNames.push(deck.name);
  }

  if (selectedNames.length === 1) {
    return selectedNames[0];
  }

  if (selectedNames.length === 2) {
    return `${selectedNames[0]}, ${selectedNames[1]}`;
  }

  return `${selectedNames[0]}, +${selectedNames.length - 1} more`;
}

function DeckPicker({
  personalDecks,
  curatedDecks,
  selectedPersonalDecks,
  selectedCuratedDecks,
  onSelectionChange,
  loading = false,
}: DeckPickerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const togglePersonalDeck = (deckName: string) => {
    const newSelection = selectedPersonalDecks.includes(deckName)
      ? selectedPersonalDecks.filter(c => c !== deckName)
      : [...selectedPersonalDecks, deckName];
    onSelectionChange({
      personalDecks: newSelection,
      curatedDecks: selectedCuratedDecks,
    });
  };

  const toggleCuratedDeck = (deckName: string) => {
    const newSelection = selectedCuratedDecks.includes(deckName)
      ? selectedCuratedDecks.filter(c => c !== deckName)
      : [...selectedCuratedDecks, deckName];
    onSelectionChange({
      personalDecks: selectedPersonalDecks,
      curatedDecks: newSelection,
    });
  };

  const clearFilters = () => {
    onSelectionChange({
      personalDecks: [],
      curatedDecks: [],
    });
  };

  const hasSelections = selectedPersonalDecks.length > 0 || selectedCuratedDecks.length > 0;
  const hasDecks = personalDecks.length > 0 || curatedDecks.length > 0;

  if (loading) {
    return (
      <div className="deck-picker deck-picker--collapsed">
        <button className="deck-picker-toggle" disabled>
          <span className="deck-picker-summary">Loading decks...</span>
        </button>
      </div>
    );
  }

  if (!hasDecks) {
    return null;
  }

  const summaryText = getSelectionSummary(
    personalDecks,
    curatedDecks,
    selectedPersonalDecks,
    selectedCuratedDecks
  );

  // Collapsed state - single line showing current selection
  if (!isExpanded) {
    return (
      <div className="deck-picker deck-picker--collapsed">
        <button
          className="deck-picker-toggle"
          onClick={() => setIsExpanded(true)}
          aria-expanded="false"
          aria-label="Expand deck picker"
        >
          <span className="deck-picker-summary">
            <span className="deck-picker-summary-label">Studying:</span>
            <span className="deck-picker-summary-value">{summaryText}</span>
          </span>
          <span className="deck-picker-expand-icon" aria-hidden="true">
            ▼
          </span>
        </button>
      </div>
    );
  }

  // Expanded state - full deck picker
  return (
    <div className="deck-picker deck-picker--expanded">
      <div className="deck-picker-header">
        <button
          className="deck-picker-toggle"
          onClick={() => setIsExpanded(false)}
          aria-expanded="true"
          aria-label="Collapse deck picker"
        >
          <span className="deck-picker-summary">
            <span className="deck-picker-summary-label">Studying:</span>
            <span className="deck-picker-summary-value">{summaryText}</span>
          </span>
          <span
            className="deck-picker-expand-icon deck-picker-expand-icon--rotated"
            aria-hidden="true"
          >
            ▼
          </span>
        </button>
        {hasSelections && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear
          </Button>
        )}
      </div>

      <div className="deck-picker-content">
        {curatedDecks.length > 0 && (
          <ChipGroup label="Built-in Decks" className="deck-section">
            {curatedDecks.map(deck => {
              const isSelected = selectedCuratedDecks.includes(deck.name.toLowerCase());
              return (
                <Chip
                  key={deck.id}
                  variant="curated"
                  selected={isSelected}
                  onClick={() => toggleCuratedDeck(deck.name.toLowerCase())}
                  badge={
                    <DeckStats
                      total={deck.cardCount}
                      struggling={deck.strugglingCount}
                      newCount={deck.newCount}
                    />
                  }
                >
                  {deck.name}
                </Chip>
              );
            })}
          </ChipGroup>
        )}

        {personalDecks.length > 0 && (
          <ChipGroup label="Your Decks" className="deck-section">
            {personalDecks.map(deck => {
              const isSelected = selectedPersonalDecks.includes(deck.name.toLowerCase());
              return (
                <Chip
                  key={deck.id}
                  variant="personal"
                  selected={isSelected}
                  onClick={() => togglePersonalDeck(deck.name.toLowerCase())}
                  badge={
                    <DeckStats
                      total={deck.cardCount}
                      struggling={deck.strugglingCount}
                      newCount={deck.newCount}
                    />
                  }
                >
                  {deck.name}
                </Chip>
              );
            })}
          </ChipGroup>
        )}
      </div>
    </div>
  );
}

export default DeckPicker;
